// What sticks while the page scrolls sits just below the app's header — never under it. The header's
// height is the AppShell's own `--app-header-h` (it measures it: banner, title, tabs), the value the
// platform's sticky bars use too; 0 without the shell. A table's own scroll box keeps `top-0`.

import type { CSSProperties } from "react";

/** A bar kept in view while the page scrolls: just below the app's header. */
export const STICKY_BELOW_HEADER = "sticky top-(--app-header-h,0px)";

/**
 * Below this window width a wide table (Admin → Routing rules needs ~944 px, plus the page's margins)
 * scrolls sideways in its box, and its header row doesn't stick: a sideways scroll box would hold it.
 */
export const NARROW_TABLE_PX = 1050;

/** The table's box: scrolls sideways when narrow; clips from 1050 px, so its header row can stick to the page. */
export const TABLE_BOX = "overflow-x-auto min-[1050px]:overflow-x-clip";

/**
 * A table's header row, from 1050 px: sticky under the app's header plus what sticks above it
 * (`--sticky-above`, set on the table's box by `stickyAbove`), below that bar (z-10 under its z-20).
 */
export const STICKY_HEADER_ROW =
  "z-10 min-[1050px]:sticky min-[1050px]:top-[calc(var(--app-header-h,0px)_+_var(--sticky-above,0px))]";

/** The height of what sticks above a table's header row (a bar, measured; 0 when nothing does), for `STICKY_HEADER_ROW`. */
export const stickyAbove = (px: number): CSSProperties =>
  ({ "--sticky-above": `${Math.max(0, Math.round(px))}px` }) as CSSProperties;

/**
 * Watches an element's height as it changes (a bar's hint swapped for its actions, a bar wrapping to two
 * lines): `onHeight` at once, then on every resize; the returned function stops it.
 */
export function watchHeight(el: Element, onHeight: (px: number) => void): () => void {
  const measure = () => onHeight(el.getBoundingClientRect().height);
  measure();
  const seen = new ResizeObserver(measure);
  seen.observe(el);
  return () => seen.disconnect();
}
